import { environment } from '../../environments/environment';
import { ProviderType } from '../models/project-integration.model';

/**
 * Provider-neutral deep-link helpers (NEX-780/781). Replaces the Jira-only
 * `jira-links.ts`; the Jira helpers remain here as the single source of truth and
 * are re-exported from `jira-links.ts` for backward compatibility.
 *
 * Origin (scheme + host + port) is preferably taken from the project's configured
 * board URL so links land in the customer's tracker; it falls back to the app-wide
 * Jira browse base when no board is configured or parseable.
 */
export function issueOrigin(boardUrl?: string | null): string {
  return originOf(boardUrl) ?? originOf(environment.jiraBrowseBase) ?? '';
}

/**
 * Builds the correct deep link for a linked issue depending on the provider:
 * Jira uses `/browse/{KEY}`, Redmine uses `/issues/{id}`.
 */
export function issueBrowseUrl(providerType: ProviderType, key: string, boardUrl?: string | null): string {
  const origin = issueOrigin(boardUrl);
  return providerType === 'REDMINE' ? `${origin}/issues/${key}` : `${origin}/browse/${key}`;
}

/**
 * Host name of the configured board, also for a board URL entered without scheme. An
 * unparseable value is shown as entered; no board yields an empty string.
 */
export function boardHost(boardUrl?: string | null): string {
  const url = boardUrl ?? '';
  if (!url) return '';
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
  } catch {
    return url;
  }
}

// ── Jira-specific helpers (single source; re-exported from jira-links.ts) ──────

export function jiraOrigin(boardUrl?: string | null): string {
  return issueOrigin(boardUrl);
}

export function jiraBrowseUrl(key: string, boardUrl?: string | null): string {
  return `${jiraOrigin(boardUrl)}/browse/${key}`;
}

export function jiraCreateIssueUrl(boardUrl?: string | null): string {
  return `${jiraOrigin(boardUrl)}/secure/CreateIssue!default.jspa`;
}

function originOf(url?: string | null): string | null {
  const raw = url?.trim();
  if (!raw) return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
}
